import { useMemo } from 'react';
import { filter } from 'lodash';
import { Tab, TabContainer, TabList, TabPanel } from 'tuui';
import { Wizard } from 'taboola-ultimate-ui';
import { FORM_MODES } from 'config/formModes';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { withFormDataProvider } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { CONVERSION_STATUS } from 'modules/tracking/config/constants';
import { codelessInitialValues } from '../config/CodelessInitialValues/codelessInitialValues';
import { conversionTabItems } from '../config/conversionTabItems';
import { CONVERSIONS_TABS } from '../config/conversionTabs';
import { STEP_IDS } from '../config/stepIds';
import { useConversions } from '../hooks/useConversions';
import { useSyncConversionsWithParent } from '../hooks/useSyncConversionsWithParent';
import { useTabStepNavigation } from '../hooks/useTabStepNavigation';
import { ButtonAnalyzer } from './ButtonAnalyzer/ButtonAnalyzer';
import { ButtonConversionLocationSelector } from './ButtonConversionLocationSelector/ButtonConversionLocationSelector';
import { ConversionEvents } from './ConversionEvents/ConversionEvents';
import { ConversionTypeSetup } from './ConversionTypeSetup/ConversionTypeSetup';
import { Frame } from './Frame';
import { GlobalButtonConversionSetup } from './GlobalButtonConversionSetup/GlobalButtonConversionSetup';
import { HeaderCodeless } from './Headers/HeaderCodeless';
import { NoEvents } from './NoEvents/NoEvents';
import { ReviewEvents } from './ReviewEvents/ReviewEvents';
import { SuccessToast } from './ReviewEvents/SuccessToast';
import { SetupClicksEventCategory } from './SetupClicksEventCategory/SetupClicksEventCategory';
import { SetupURLEvent } from './SetupURLEvent/SetupURLEvent';
import { SetupValue } from './SetupValue/SetupValue';
import styles from './conversionSetup.module.scss';

const getFrameStyle = currentStepId => {
    if (currentStepId === STEP_IDS.SUCCESS_TOAST) {
        return 'toast-frame';
    } else if (currentStepId === STEP_IDS.BUTTON_ANALYZER_STEP) {
        return 'floating-frame';
    }
    return 'frame-codeless';
};

const ConversionSetup = () => {
    const {
        activeTabId,
        currentStepId,
        currentConversion,
        navigateToStepId,
        switchTab,
        handleStartCreateNewEvent,
        handleEditEvent,
        handleCancelWizard,
        isNavigateMode,
        handleNavigateMode,
        updateDraftEvent,
        handleSaveDraftToPersistent,
    } = useTabStepNavigation(conversionTabItems);

    const { persistentConversions, existingConversions, unfilteredFetchedConversions } = useConversions();
    const { setFocusedConversionId } = useSyncConversionsWithParent(persistentConversions, currentConversion);
    const isValueFeatureEnabled = useConfigMatch({ [FEATURE_FLAGS.CODELESS_VALUE_ENABLED]: 'true' });

    const tabHeaders = useMemo(
        () =>
            conversionTabItems.map(tab => ({
                id: tab.id,
                caption: tab.caption,
                onClick: () => switchTab(tab.id),
                isActive: tab.id === activeTabId,
            })),
        [activeTabId, switchTab]
    );

    const frameStyle = getFrameStyle(currentStepId);

    const renderNonTabbedStepContent = () => {
        const commonStepProps = {
            currentConversion,
            navigateToStepId,
            handleCancelWizard,
            isValueFeatureEnabled,
            updateDraftEvent,
            handleSaveDraftToPersistent,
            setFocusedConversionId,
        };

        const stepRenderMap = {
            [STEP_IDS.CONVERSION_TYPE_SETUP]: () => <ConversionTypeSetup {...commonStepProps} />,
            [STEP_IDS.BUTTON_ANALYZER_STEP]: () => <ButtonAnalyzer {...commonStepProps} />,
            [STEP_IDS.SETUP_URL_EVENT]: () => <SetupURLEvent {...commonStepProps} />,
            [STEP_IDS.SETUP_CLICKS_EVENT_CATEGORY]: () => <SetupClicksEventCategory {...commonStepProps} />,
            [STEP_IDS.SETUP_VALUE]: () => <SetupValue {...commonStepProps} />,
            [STEP_IDS.BUTTON_CONVERSION_LOCATION_SELECTOR]: () => (
                <ButtonConversionLocationSelector {...commonStepProps} />
            ),
            [STEP_IDS.GLOBAL_BUTTON_SETUP]: () => <GlobalButtonConversionSetup {...commonStepProps} s />,
            [STEP_IDS.REVIEW_EVENTS]: () => (
                <ReviewEvents
                    handleEditEvent={handleEditEvent}
                    navigateToStepId={navigateToStepId}
                    setFocusedConversionId={setFocusedConversionId}
                    unfilteredFetchedConversions={unfilteredFetchedConversions}
                />
            ),
            [STEP_IDS.SUCCESS_TOAST]: () => <SuccessToast {...commonStepProps} />,
        };

        const renderStepComponent = stepRenderMap[currentStepId];
        if (!renderStepComponent) {
            return null;
        }

        return renderStepComponent();
    };

    return (
        <Frame frameStyle={frameStyle}>
            {currentStepId === STEP_IDS.VIEW_EVENTS_LIST ? (
                <>
                    <HeaderCodeless
                        navigateToStepId={navigateToStepId}
                        isNavigateMode={isNavigateMode}
                        handleNavigateMode={handleNavigateMode}
                    />
                    {!isNavigateMode && (
                        <TabContainer>
                            <div className={styles['tab-list-container']}>
                                <TabList>
                                    {tabHeaders.map(tabProps => (
                                        <Tab key={tabProps.id} onClick={tabProps.onClick} selected={tabProps.isActive}>
                                            <FormattedMessage {...tabProps.caption} />
                                        </Tab>
                                    ))}
                                </TabList>
                            </div>
                            {conversionTabItems.map(tab => {
                                let conversionsForThisTabPanel;
                                if (tab.id === CONVERSIONS_TABS.ALL_YOUR_EVENTS) {
                                    conversionsForThisTabPanel = filter(
                                        persistentConversions,
                                        event => event.status !== CONVERSION_STATUS.ARCHIVED
                                    );
                                } else {
                                    conversionsForThisTabPanel = existingConversions;
                                }

                                const panelContent =
                                    conversionsForThisTabPanel && conversionsForThisTabPanel.length > 0 ? (
                                        <ConversionEvents
                                            conversions={conversionsForThisTabPanel}
                                            existingConversionsToEdit={existingConversions}
                                            handleEditClick={handleEditEvent}
                                            setFocusedConversionId={setFocusedConversionId}
                                            handleStartCreateNewEvent={handleStartCreateNewEvent}
                                        />
                                    ) : (
                                        <NoEvents onStartCreate={handleStartCreateNewEvent} />
                                    );

                                return (
                                    <TabPanel key={tab.id}>
                                        <div className={styles['tab-panel-content']}>{panelContent}</div>
                                    </TabPanel>
                                );
                            })}
                        </TabContainer>
                    )}
                </>
            ) : (
                renderNonTabbedStepContent()
            )}
        </Frame>
    );
};

const ConversionSetupWithFormDataProvider = withFormDataProvider(ConversionSetup, {
    defaultValuesConfig: codelessInitialValues,
    formContainerId: Wizard.WIZARD_CONTAINER_ID,
    mode: FORM_MODES.CREATE,
    isNetworkOwnerAllowed: true,
});

export { ConversionSetupWithFormDataProvider as ConversionSetup };
